// Anthropic's vision pipeline internally resizes to fit a 1568×1568 bounding
// box, so sending anything larger wastes bandwidth and image tokens without
// improving extraction quality.  Matching their limit shaves ~30% off file
// size and cuts end-to-end round-trip time by several seconds.
const MAX_EDGE = 1568;
const OUTPUT_TYPE = 'image/jpeg';
const OUTPUT_QUALITY = 0.92;

export type ImagePrepErrorCode =
  | 'heic_unsupported'
  | 'not_an_image'
  | 'decode_failed';

export class ImagePrepError extends Error {
  code: ImagePrepErrorCode;
  constructor(code: ImagePrepErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = 'ImagePrepError';
  }
}

export interface PreparedImage {
  blob: Blob;
  width: number;
  height: number;
  fileName: string;
  fileType: string;
}

function isHeic(file: File): boolean {
  const t = (file.type || '').toLowerCase();
  if (t === 'image/heic' || t === 'image/heif') return true;
  const n = file.name.toLowerCase();
  return n.endsWith('.heic') || n.endsWith('.heif');
}

function isPdf(file: File): boolean {
  const t = (file.type || '').toLowerCase();
  if (t === 'application/pdf') return true;
  return file.name.toLowerCase().endsWith('.pdf');
}

async function transcodeHeic(file: File): Promise<File> {
  // Lazy import — the ~40 KB WASM + JS only loads when the user actually
  // picks a HEIC photo (almost exclusively iOS "High Efficiency" camera).
  let heicToFn: typeof import('heic-to').heicTo;
  try {
    ({ heicTo: heicToFn } = await import('heic-to'));
  } catch (err) {
    console.error('[imagePrep] heic-to import failed', err);
    throw new ImagePrepError(
      'heic_unsupported',
      "This photo is in a format we couldn't read. Please retake it — or pick a different one from your gallery.",
    );
  }
  try {
    const jpegBlob = await heicToFn({
      blob: file,
      type: 'image/jpeg',
      quality: 0.95,
    });
    const baseName = stripExtension(file.name) || 'prescription';
    return new File([jpegBlob], `${baseName}.jpg`, { type: 'image/jpeg' });
  } catch (err) {
    console.error('[imagePrep] HEIC transcode failed', err);
    throw new ImagePrepError(
      'decode_failed',
      "We couldn't open that photo. Try taking it again.",
    );
  }
}

/**
 * Downscale to ≤2000px on the long edge and re-encode as JPEG.
 * Canvas re-encode strips EXIF (including GPS) — CLAUDE.md §5 PHI rules.
 * Handles HEIC/HEIF from iPhone cameras by transcoding first.
 * PDFs pass through untouched — Claude accepts them as document blocks.
 */
export async function prepareImage(file: File): Promise<PreparedImage> {
  if (isPdf(file)) {
    return {
      blob: file,
      width: 0,
      height: 0,
      fileName: stripExtension(file.name || 'prescription') + '.pdf',
      fileType: 'application/pdf',
    };
  }
  const source = isHeic(file) ? await transcodeHeic(file) : file;
  if (source.type && !source.type.startsWith('image/')) {
    throw new ImagePrepError(
      'not_an_image',
      "That file isn't an image or PDF. Please pick a different file.",
    );
  }
  let bitmap: ImageBitmap;
  try {
    bitmap = await loadBitmap(source);
  } catch (err) {
    console.error('[imagePrep] decode failed', err);
    throw new ImagePrepError(
      'decode_failed',
      "We couldn't open that image. Try taking the photo again.",
    );
  }
  const { width, height } = fitWithin(bitmap.width, bitmap.height, MAX_EDGE);

  const canvas =
    typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(width, height)
      : Object.assign(document.createElement('canvas'), { width, height });

  const ctx = (canvas as HTMLCanvasElement | OffscreenCanvas).getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  (ctx as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D).drawImage(
    bitmap,
    0,
    0,
    width,
    height,
  );
  bitmap.close?.();

  const blob = await toBlob(canvas, OUTPUT_TYPE, OUTPUT_QUALITY);
  const baseName = stripExtension(source.name) || 'prescription';
  return {
    blob,
    width,
    height,
    fileName: `${baseName}.jpg`,
    fileType: OUTPUT_TYPE,
  };
}

async function loadBitmap(file: File): Promise<ImageBitmap> {
  // `imageOrientation: 'from-image'` honors EXIF rotation so landscape photos
  // from iOS/Android don't reach Claude sideways. Chromium/Firefox/Safari 15+
  // support it; older Safari falls through to the fallback path.
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Some older browsers throw on the options arg — retry without.
    }
    try {
      return await createImageBitmap(file);
    } catch {
      // Fall through to the URL/decode path.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return (await createImageBitmap(img)) as ImageBitmap;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function fitWithin(w: number, h: number, max: number) {
  if (w <= max && h <= max) return { width: w, height: h };
  const ratio = w > h ? max / w : max / h;
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}

async function toBlob(
  canvas: HTMLCanvasElement | OffscreenCanvas,
  type: string,
  quality: number,
): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type, quality });
  }
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) =>
        b ? resolve(b) : reject(new Error('Canvas toBlob returned null')),
      type,
      quality,
    );
  });
}

function stripExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}
